import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatINR } from "@/lib/api";
import Customer360Card from "@/components/clients/Customer360Card";
import { IndianRupee, Clock, Zap, CheckCircle2, CircleDot, AlertTriangle, Flag } from "lucide-react";

const fmtDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) +
    ", " + d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
};

const PayoutCard = ({ p, leadId }) => {
  if (!p || p.status === "unavailable") return null;
  const boostExtra = p.boost?.extra || 0;
  let headline, sub;
  if (p.status === "paid") {
    headline = formatINR(p.amount);
    sub = "Commission paid to your wallet";
  } else if (p.status === "needs_basis") {
    headline = `${p.pct_min}–${p.pct_max}%`;
    sub = `of the ${p.basis_label} — exact ₹ will show once the amount is confirmed`;
  } else if (p.min != null && p.max != null && p.min !== p.max) {
    headline = `${formatINR(p.min + boostExtra)} – ${formatINR(p.max + boostExtra)}`;
    sub = p.basis ? `${p.pct_min}–${p.pct_max}% of ${formatINR(p.basis)}` : "based on this product's payout range";
  } else {
    const v = (p.max ?? p.min ?? 0) + boostExtra;
    headline = formatINR(v);
    sub = p.basis ? `${p.pct_max}% of ${formatINR(p.basis)}` : "flat payout for this product";
  }
  return (
    <div className={`rounded-xl border p-4 ${p.status === "paid" ? "bg-emerald-50 border-emerald-200" : "bg-white border-amber-200"}`}
      data-testid={`lead-expected-payout-${leadId}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-stone-500">
        <IndianRupee className="w-3.5 h-3.5 text-emerald-700" />
        {p.status === "paid" ? "You earned" : "Expected payout if it converts"}
      </div>
      <div className="font-display text-2xl font-black font-mono text-emerald-900 mt-1">{headline}</div>
      <div className="text-xs text-stone-500 mt-0.5">{sub}</div>
      {p.boost && boostExtra > 0 && (
        <div className="mt-2 inline-flex items-center gap-1 bg-violet-100 text-violet-800 text-[11px] font-bold px-2.5 py-1 rounded-full" data-testid={`lead-payout-boost-${leadId}`}>
          <Zap className="w-3 h-3" /> Includes {formatINR(boostExtra)} personal boost
        </div>
      )}
      {p.status !== "paid" && p.payable_at && (
        <div className="text-[11px] text-stone-500 mt-2">
          💰 Payable when this lead reaches <b className="text-stone-700">"{p.payable_at}"</b> with verified proof
        </div>
      )}
    </div>
  );
};

const Timeline = ({ detail, leadId }) => {
  const events = [
    ...(detail.stages || []).map((s) => ({
      at: s.at, title: s.stage, note: s.note, by: s.by_name, kind: "stage",
    })),
    ...(detail.activity || [])
      .filter((a) => a.action !== "stage_change")
      .map((a) => ({
        at: a.at, title: a.detail, by: a.by_name, kind: a.action,
      })),
  ].sort((a, b) => (a.at || "").localeCompare(b.at || ""));

  if (events.length === 0) return <div className="text-xs text-stone-400">No timeline events yet.</div>;
  return (
    <ol className="relative border-l-2 border-stone-200 ml-1.5 space-y-4" data-testid={`lead-timeline-${leadId}`}>
      {events.map((e, i) => {
        const isLast = i === events.length - 1;
        const isBreach = e.kind === "sla_breach";
        return (
          <li key={i} className="pl-4 relative" data-testid={`lead-timeline-event-${leadId}-${i}`}>
            <span className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full border-2 border-white ${isBreach ? "bg-red-500" : isLast ? "bg-emerald-600" : "bg-stone-300"}`} />
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className={`text-sm font-bold ${isBreach ? "text-red-700" : isLast ? "text-emerald-900" : "text-stone-800"}`}>
                {isBreach && <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}{e.title}
              </span>
              <span className="text-[11px] font-mono text-stone-400">{fmtDT(e.at)}</span>
            </div>
            {(e.note || (e.by && e.by !== "System")) && (
              <div className="text-[11px] text-stone-500 mt-0.5">
                {e.note}{e.note && e.by && e.by !== "System" ? " · " : ""}{e.by && e.by !== "System" ? `by ${e.by}` : ""}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default function LeadDetailPanel({ lead }) {
  const [detail, setDetail] = useState(null);
  const load = () => api.get(`/leads/${lead.id}/detail`).then((r) => setDetail(r.data)).catch(() => {});
  useEffect(() => { load(); }, [lead.id, lead.status, lead.stage]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestCorrection = async () => {
    const reason = window.prompt("What's wrong with this paid lead? (e.g. wrong amount credited, wrong document, duplicate)") || "";
    if (!reason.trim()) return;
    try {
      await api.post(`/leads/${lead.id}/correction-request`, { reason });
      toast.success("Correction request sent — Finance will review it");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send request"); }
  };

  if (!detail) return null;
  return (
    <div className="space-y-5">
    <div className="grid lg:grid-cols-5 gap-5" data-testid={`lead-detail-panel-${lead.id}`}>
      <div className="lg:col-span-3">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-3.5 h-3.5 text-stone-400" />
          <span className="text-xs font-black uppercase text-stone-500">Journey timeline</span>
          {detail.days_in_pipeline != null && (
            <span className="text-[10px] font-bold bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full" data-testid={`lead-days-pipeline-${lead.id}`}>
              {detail.days_in_pipeline === 0 ? "started today" : `${detail.days_in_pipeline} day${detail.days_in_pipeline > 1 ? "s" : ""} in pipeline`}
            </span>
          )}
        </div>
        <Timeline detail={detail} leadId={lead.id} />
      </div>
      <div className="lg:col-span-2 space-y-3">
        {detail.expected_payout && <PayoutCard p={detail.expected_payout} leadId={lead.id} />}
        {detail.status === "Paid" && (
          detail.open_correction_request ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-900" data-testid={`lead-correction-open-${lead.id}`}>
              <Flag className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
              <b>Correction requested</b> — Finance is reviewing: "{detail.open_correction_request.reason}"
            </div>
          ) : (
            <button onClick={requestCorrection} data-testid={`lead-request-correction-${lead.id}`}
              className="text-xs font-bold text-amber-800 underline underline-offset-2 inline-flex items-center gap-1">
              <Flag className="w-3.5 h-3.5" /> Something wrong with this payout? Request correction
            </button>
          )
        )}
        {detail.status !== "Paid" && detail.status !== "Rejected" && (
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-2">
              <CircleDot className="w-3.5 h-3.5 text-emerald-700" /> What happens next
            </div>
            <ul className="text-xs text-stone-600 space-y-1.5">
              {detail.proof_status !== "verified" && (
                <li className="flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-px" /> Upload completion proof below so Ops can verify it</li>
              )}
              <li className="flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-px" /> Lead progresses to <b>"{detail.payable_stage}"</b> — commission becomes payable</li>
              <li className="flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-px" /> Finance reconciles it and the amount lands in your wallet</li>
            </ul>
          </div>
        )}
      </div>
    </div>
    <Customer360Card leadId={lead.id} compact />
    </div>
  );
}
